import type { TLRecord, TLShape, TLShapeId } from "@tldraw/tlschema";

export const MAX_SKETCH_SHAPES = 100;
export const SKETCH_PREVIEW_SIZE = 2048;
const drawingTypes = new Set([
  "draw",
  "geo",
  "line",
  "arrow",
  "highlight",
  "mark",
  "drafting",
]);
const compare = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

export function isSketchSelection(shapes: readonly TLShape[]) {
  return (
    shapes.length > 0 &&
    shapes.length <= MAX_SKETCH_SHAPES &&
    shapes.some((shape) => drawingTypes.has(shape.type)) &&
    shapes.every(
      (shape) => drawingTypes.has(shape.type) || shape.type === "text",
    )
  );
}

// Stable across browser/server record property order. Include ancestors so a moved,
// hidden or locked sheet cannot silently change the selection after its preview.
function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === "object")
    return Object.fromEntries(
      Object.entries(value)
        .sort(([a], [b]) => compare(a, b))
        .map(([k, v]) => [k, canonical(v)]),
    );
  return value;
}

export function sketchSelection(
  ids: readonly string[],
  record: (id: string) => TLRecord | undefined,
) {
  if (
    !ids.length ||
    ids.length > MAX_SKETCH_SHAPES ||
    new Set(ids).size !== ids.length
  )
    throw new Error("Select up to 100 drawing objects.");
  const shapes = [...ids].sort().map((id) => record(id));
  if (
    shapes.some((shape) => shape?.typeName !== "shape") ||
    !isSketchSelection(shapes as TLShape[])
  )
    throw new Error("Select drawing strokes and shapes, with optional text.");
  const records = new Map<string, unknown>();
  for (const shape of shapes as TLShape[]) {
    let current: TLRecord | undefined = shape;
    let depth = 0;
    while (current?.typeName === "shape") {
      if (
        ++depth > 100 ||
        current.isLocked ||
        current.opacity === 0 ||
        (current.type === "trace" && current.props.hidden)
      )
        throw new Error("Choose visible, unlocked drawing objects.");
      const {
        id,
        type,
        parentId,
        x,
        y,
        rotation,
        opacity,
        index,
        props,
        meta,
      } = current;
      // Ancestor contents are excluded; only their transforms/visibility affect this snapshot.
      records.set(id, {
        id,
        type,
        parentId,
        x,
        y,
        rotation,
        opacity,
        index,
        ...(ids.includes(id) ? { props, meta } : {}),
      });
      current = record(current.parentId);
    }
  }
  const key = JSON.stringify(
    canonical([...records.entries()].sort(([a], [b]) => compare(a, b))),
  );
  if (key.length > 1_000_000)
    throw new Error("This sketch is too detailed. Select a smaller part.");
  return { ids: [...ids].sort() as TLShapeId[], key };
}

export const SKETCH_PROMPT =
  "Turn this sketch into clean architectural linework. Keep its proportions, layout and viewing angle. Use fine black lines on a plain white background.";
export const SKETCH_INSTRUCTION =
  "Convert the supplied rough sketch into one clean architectural drawing asset. Follow the user's description. Preserve the sketch's silhouette, relative proportions, arrangement and viewing direction. Do not invent rooms, furniture, labels or dimensions that were not requested. Use clean linework on a plain white background, without shadows, paper texture, a border or a checkerboard. This is a conceptual image, not a dimensionally verified plan.";
